import { useEffect, useState } from "react";
import MainLayout from "../../components/layouts/MainLayout";
import { Link, useNavigate, useParams } from "react-router-dom";
import { message, Spin, Alert } from "antd";
import Swal from "sweetalert2";
import { LeftCircleOutlined } from "@ant-design/icons";
import { Auth } from "../../utils/Auth";
import { DeviseApi } from "../../api/deviseApi";

const UpdateDeviseView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const abortController = new AbortController();

    const [formData, setFormData] = useState({
        name: '',
        iso_code: '',
        symbol: '',
        country_region: '',
        decimal_places: 2,
        symbol_position: 'after',
        exchange_rate: '',
        rate_mode: 'MANUAL',
    });
    const [isDefault, setIsDefault] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [error, setError] = useState(null);

    const fetchData = async () => {
        setFetching(true);
        setError(null);
        try {
            const res = await DeviseApi.getDeviseDetails(id, abortController.signal);
            const devise = res.data;
            setFormData({
                name: devise.name,
                iso_code: devise.iso_code,
                symbol: devise.symbol,
                country_region: devise.country_region ?? '',
                decimal_places: devise.decimal_places,
                symbol_position: devise.symbol_position,
                exchange_rate: devise.exchange_rate,
                rate_mode: devise.rate_mode,
            });
            setIsDefault(devise.is_default);
        } catch (err) {
            setError("Impossible de charger la devise.");
        } finally {
            setFetching(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await DeviseApi.updateDevise(id, JSON.stringify(formData), abortController.signal);

            if (!res.success) {
                setLoading(false);
                return Swal.fire({ title: "Impossible d'enregistrer", text: res.message });
            }

            message.success('Devise mise à jour avec succès');
            navigate(`/devises/${id}/details`);
        } catch (error) {
            setLoading(false);
            message.error("Une erreur est survenue lors de la mise à jour de la devise");
            const messages = await error?.messages ?? error?.message;

            if (messages instanceof Array)
                return Swal.fire({
                    title: messages.join('\n') !== 'Unauthenticated' ? "Erreur détectée" : "Session expirée",
                    text: messages.join('\n') !== 'Unauthenticated' ? "Veuillez vous reconncter." : "Votre session a expirée veuillez vous reconnecter",
                    icon: "error",
                }).then(() => {
                    if (messages.join('\n') === 'Unauthenticated') {
                        Auth.removeSessionToken();
                        return navigate(`/login`);
                    }
                });
        } finally {
            setLoading(false);
        }
    };

    return <MainLayout>
        <div className="container mx-auto p-6">
            <div className="flex justify-between">
                <h1 className="text-3xl font-semibold mb-6">Modifier la devise</h1>
                <Link to={`/devises`}><LeftCircleOutlined /> Retour  </Link>
            </div>

            {fetching && <Spin size="large" />}
            {error && <Alert message={error} type="error" />}

            {!fetching && !error && (
                <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md space-y-4">
                    {isDefault && (
                        <Alert
                            type="info"
                            showIcon
                            message="Cette devise est la devise de référence de la plateforme. Son taux de change reste fixé à 1."
                        />
                    )}

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">Nom</label>
                            <input type="text" name="name" value={formData.name} onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md" required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Code ISO</label>
                            <input type="text" name="iso_code" value={formData.iso_code} onChange={handleChange}
                                maxLength={3} className="w-full px-4 py-2 border border-gray-300 rounded-md uppercase" required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Symbole</label>
                            <input type="text" name="symbol" value={formData.symbol} onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md" required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Pays / région</label>
                            <input type="text" name="country_region" value={formData.country_region} onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Nombre de décimales</label>
                            <input type="number" name="decimal_places" value={formData.decimal_places} onChange={handleChange}
                                min={0} max={4} className="w-full px-4 py-2 border border-gray-300 rounded-md" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Position du symbole</label>
                            <select name="symbol_position" value={formData.symbol_position} onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md">
                                <option value="before">Avant le montant</option>
                                <option value="after">Après le montant</option>
                            </select>
                        </div>
                        {!isDefault && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Mode de mise à jour du taux</label>
                                    <select name="rate_mode" value={formData.rate_mode} onChange={handleChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-md">
                                        <option value="MANUAL">Manuel</option>
                                        <option value="AUTO">Automatique (Frankfurter, si couvert)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Taux de change (vs devise de référence)</label>
                                    <input type="number" step="0.00000001" name="exchange_rate" value={formData.exchange_rate} onChange={handleChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-md" />
                                </div>
                            </>
                        )}
                    </div>

                    <button type="submit" disabled={loading}
                        className="mt-4 px-4 py-2 text-white bg-africasa-secondary rounded hover:bg-africasa-primary disabled:opacity-50">
                        {loading ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                </form>
            )}
        </div>
    </MainLayout>
}

export default UpdateDeviseView
